import React, { useState, useEffect } from "react";
import {
  Box,
  Breadcrumbs,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  CardMedia,
  Link,
  Container,
  Pagination,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Home, NavigateNext, ExpandMore } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { productService } from "../services/products";
import { headerCategoriesService, HeaderCategory } from "../services/headerCategories";
import { subcategoriesService, Subcategory, SubcategoryTreeNode } from "../services/subcategories";
import { Product } from "../types";
import { getCategoryFromParam, getCategoryBreadcrumbLabel } from "../utils/breadcrumbs";
import { buildCategoryRoute, parseCategoryRoute } from "../utils/categoryRoutes";
import { useSeoMeta } from "../utils/seoMeta";
import { resolveImageUrl, withImageFallback } from "../utils/imageFallback";

const CONDITIONS = [
  {
    id: "brandNew",
    dbValues: ["excellent"] as Array<Product["condition"]>,
    labelKey: "products.categoryDetails.conditions.brandNew",
  },
  {
    id: "perfectCondition",
    dbValues: ["excellent"] as Array<Product["condition"]>,
    labelKey: "products.categoryDetails.conditions.perfectCondition",
  },
  {
    id: "goodCondition",
    dbValues: ["good"] as Array<Product["condition"]>,
    labelKey: "products.categoryDetails.conditions.goodCondition",
  },
  {
    id: "fairCondition",
    dbValues: ["fair"] as Array<Product["condition"]>,
    labelKey: "products.categoryDetails.conditions.fairCondition",
  },
];

const YEARS = Array.from({ length: 21 }, (_, i) => 2025 - i);

const BRANDS = [
  "3M",
  "Abbott",
  "Accutome",
  "AEONMED",
  "Aesculap",
  "AGFA",
  "Alcon",
  "Ambu",
  "Canon Medical System",
  "Carestream",
  "Covidien",
  "Dräger",
  "Esaote",
  "Fujifilm",
  "General Electric HealthCare",
];

const SPECIALTIES = [
  "Cardiology",
  "Dentistry",
  "Dermatology",
  "Emergency Medicine",
  "ENT",
  "Gastroenterology",
  "General Medicine",
  "Gynecology and Obstetrics",
  "Neurology",
  "Oncology",
  "Radiology",
  "Surgery",
  "Veterinary Medicine",
];

const PRODUCTS_PER_PAGE = 10;

const formatPrice = (price: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  }).format(price);
};

function CategoryDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId, "*": subcategoryPath } = useParams<{
    categoryId: string;
    "*"?: string;
  }>();
  const { t } = useTranslation();

  const subcategorySegments = subcategoryPath
    ? subcategoryPath.split("/").filter(Boolean)
    : parseCategoryRoute(location.pathname).subcategorySegments;

  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [headerCategories, setHeaderCategories] = useState<HeaderCategory[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);

  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([10, 608590]);

  // When navigating to a nested subcategory route, auto-select it and its descendants
  useEffect(() => {
    if (subcategorySegments.length > 0 && subcategories.length > 0 && categoryId) {
      const activeNode = subcategoriesService.resolveNodeFromPath(
        subcategories,
        categoryId,
        subcategorySegments,
      );
      if (activeNode) {
        const descendantKeys = subcategoriesService.getDescendantKeys(
          subcategories,
          activeNode.key,
        );
        setSelectedSubcategories(descendantKeys);
      }
    } else if (selectedSubcategories.length > 0 && subcategorySegments.length === 0) {
      setSelectedSubcategories([]);
    }
  }, [subcategorySegments.join("/"), subcategories.length, categoryId]);

  useEffect(() => {
    if (categoryId && categoryId !== "all") {
      setExpandedCategory(categoryId);
    } else {
      setExpandedCategory(false);
    }
  }, [categoryId]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const categoryFilter = getCategoryFromParam(categoryId);
        const isAllCategory = !categoryId || categoryId === "all";
        const { data, error } = await productService.getProducts({
          category: isAllCategory ? undefined : categoryFilter,
          status: "available",
        });
        if (error) {
          console.error("Failed to load products for category", error);
          setError(t("products.failedToLoad"));
          setProducts([]);
        } else {
          setProducts(data || []);
        }
      } catch (err) {
        console.error("Unexpected error while loading products", err);
        setError(t("products.unexpectedError"));
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategoriesAndSubcategories = async () => {
      const [catsRes, subsRes] = await Promise.all([
        headerCategoriesService.getActiveCategories(),
        subcategoriesService.getActiveSubcategories(),
      ]);
      if (catsRes.data) setHeaderCategories(catsRes.data);
      if (subsRes.data) setSubcategories(subsRes.data);
    };

    fetchProducts();
    fetchCategoriesAndSubcategories();
    setPage(1);
  }, [categoryId, t]);

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filteredProducts = products.filter((product) => {
    const numericPrice =
      typeof product.zetta_price === "number" && !isNaN(product.zetta_price)
        ? product.zetta_price
        : product.price || 0;
    if (numericPrice < priceRange[0] || numericPrice > priceRange[1]) {
      return false;
    }

    if (selectedSubcategories.length) {
      const productSubcategory = (product as Product & { subcategory_key?: string }).subcategory_key;
      if (!productSubcategory || !selectedSubcategories.includes(productSubcategory)) {
        return false;
      }
    }

    if (selectedConditions.length) {
      const selectedDbConditions = new Set<Product["condition"]>();
      CONDITIONS.forEach((option) => {
        if (selectedConditions.includes(option.id)) {
          option.dbValues.forEach((c) => selectedDbConditions.add(c));
        }
      });
      if (selectedDbConditions.size && !selectedDbConditions.has(product.condition)) {
        return false;
      }
    }

    if (selectedYears.length) {
      const year = product.year;
      if (!year || !selectedYears.includes(year)) {
        return false;
      }
    }

    if (selectedBrands.length) {
      const brand = product.brand;
      if (!brand || !selectedBrands.includes(brand)) {
        return false;
      }
    }

    if (selectedSpecialties.length) {
      const specialty = product.specialty;
      if (!specialty || !selectedSpecialties.includes(specialty)) {
        return false;
      }
    }

    return true;
  });

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));

  const activeSubcategory =
    categoryId && subcategorySegments.length > 0
      ? subcategoriesService.resolveNodeFromPath(
          subcategories,
          categoryId,
          subcategorySegments,
        )
      : null;

  const breadcrumbTrail: { label: string; route: string | null }[] = [];
  if (!categoryId || categoryId === "all") {
    breadcrumbTrail.push({
      label: t("products.allProducts", { defaultValue: "All products" }),
      route: "/category/all",
    });
  } else {
    breadcrumbTrail.push({
      label: getCategoryBreadcrumbLabel(t, categoryId),
      route: buildCategoryRoute(categoryId),
    });

    if (activeSubcategory && categoryId) {
      const ancestors: SubcategoryTreeNode[] = [];
      const tree = subcategoriesService.buildTree(
        subcategories.filter((s) => s.category_key === categoryId),
      );

      const findAncestors = (
        nodes: SubcategoryTreeNode[],
        targetId: string,
        chain: SubcategoryTreeNode[],
      ): boolean => {
        for (const node of nodes) {
          const next = [...chain, node];
          if (node.id === targetId) {
            ancestors.push(...next);
            return true;
          }
          if (findAncestors(node.children, targetId, next)) return true;
        }
        return false;
      };
      findAncestors(tree, activeSubcategory.id, []);

      ancestors.forEach((node, index) => {
        const segments = ancestors
          .slice(0, index + 1)
          .map((n) => subcategoriesService.getRouteSegment(n));
        breadcrumbTrail.push({
          label: node.label_en || node.key,
          route: buildCategoryRoute(categoryId, segments),
        });
      });
    }
  }

  useSeoMeta({
    title: activeSubcategory?.meta_title || undefined,
    description: activeSubcategory?.meta_description || undefined,
    canonicalUrl:
      typeof window !== "undefined"
        ? `${window.location.origin}${location.pathname}`
        : undefined,
  });

  const renderSubcategorySidebar = (
    nodes: SubcategoryTreeNode[],
    categoryKey: string,
    depth = 0,
  ): React.ReactNode =>
    nodes.map((node) => {
      const segments = subcategoriesService.getPathSegments(
        subcategories,
        node.key,
        categoryKey,
      );
      const route = buildCategoryRoute(categoryKey, segments);
      const isActive = activeSubcategory?.id === node.id;

      return (
        <Box key={node.id} sx={{ pl: depth * 1.5 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => route && navigate(route)}
            sx={{
              display: "block",
              textAlign: "left",
              py: 0.5,
              pl: depth > 0 ? 1 : 0,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? "primary.main" : "text.primary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
            }}
          >
            {node.label_en || node.key}
          </Link>
          {node.children.length > 0 &&
            renderSubcategorySidebar(node.children, categoryKey, depth + 1)}
        </Box>
      );
    });

  return (
    <>
      <Container maxWidth="xl">
        <Box sx={{ minHeight: "100vh", p: { xs: 2, sm: 4, md: 6 } }}>
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" />}
            sx={{ mb: 4 }}
          >
            <Link
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              color="inherit"
              onClick={() => navigate("/")}
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              {t("nav.home")}
            </Link>
            {breadcrumbTrail.map((crumb, index) => {
              const isLast = index === breadcrumbTrail.length - 1;
              if (isLast || !crumb.route) {
                return (
                  <Typography key={crumb.label} color="text.primary">
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Link
                  key={crumb.route}
                  color="inherit"
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(crumb.route!)}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0 }}>
              <Box sx={{ pb: 2, mb: 2 }}>
                <Typography fontWeight={600} mb={2}>
                  {t("products.categories", { defaultValue: "Categories" })}
                </Typography>
                {headerCategories.map((cat) => {
                  const catTree = subcategoriesService.buildTree(
                    subcategories.filter((s) => s.category_key === cat.key),
                  );
                  const isExpanded = expandedCategory === cat.key;

                  const handleAccordionChange = (expanded: boolean) => {
                    setExpandedCategory(expanded ? cat.key : false);
                    if (!expanded) {
                      const catKeys = subcategories
                        .filter((s) => s.category_key === cat.key)
                        .map((s) => s.key);
                      setSelectedSubcategories((current) =>
                        current.filter((key) => !catKeys.includes(key)),
                      );
                    }
                  };

                  return (
                    <Accordion key={cat.id} expanded={isExpanded} onChange={(e, expanded) => handleAccordionChange(expanded)} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', border: 'none', '&.MuiAccordion-root': { border: 'none' }, '&.MuiPaper-root': { border: 'none' }, '&:hover': { boxShadow: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0, minHeight: '40px', '& .MuiAccordionSummary-content': { my: 1 } }}>
                        <Link
                          component="button"
                          variant="body1"
                          onClick={(e) => {
                            e.stopPropagation();
                            const route = buildCategoryRoute(cat.key);
                            if (route) navigate(route);
                          }}
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.95rem',
                            textAlign: 'left',
                            color: categoryId === cat.key ? 'primary.main' : 'text.primary',
                            textDecoration: 'none',
                          }}
                        >
                          {cat.label_en || cat.key}
                        </Link>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 5, py: 0, pl: 2 }}>
                        {catTree.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            {t("common.noItems", { defaultValue: "No subcategories" })}
                          </Typography>
                        ) : (
                          renderSubcategorySidebar(catTree, cat.key)
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>

              <Box sx={{ pb: 2, mb: 2, borderBottom: "1px solid var(--border-primary)" }}>
                <Typography fontWeight={600} mb={1}>
                  {t("products.priceRange")}
                </Typography>
                <Typography variant="body2">
                  {priceRange[0]} € – {priceRange[1]} €
                </Typography>
                <Slider
                  min={10}
                  max={608590}
                  value={priceRange}
                  onChange={(_, newValue) =>
                    setPriceRange(newValue as [number, number])
                  }
                  valueLabelDisplay="auto"
                  sx={{ mt: 1, width: "100%" }}
                />
              </Box>

              <Box sx={{ pb: 2, mb: 2, borderBottom: "1px solid var(--border-primary)" }}>
                <Typography fontWeight={600} mb={1}>
                  {t("products.condition")}
                </Typography>
                {CONDITIONS.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedConditions.includes(item.id)}
                        onChange={(e) =>
                          e.target.checked
                            ? setSelectedConditions([...selectedConditions, item.id])
                            : setSelectedConditions(
                                selectedConditions.filter((c) => c !== item.id)
                              )
                        }
                      />
                    }
                    label={t(item.labelKey)}
                    sx={{ display: "block" }}
                  />
                ))}
              </Box>

              <Box sx={{ pb: 2, mb: 2, borderBottom: "1px solid var(--border-primary)" }}>
                <Typography fontWeight={600} mb={1}>
                  {t("products.year")}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    pr: 1,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {YEARS.map((year) => (
                    <FormControlLabel
                      key={year}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedYears.includes(year)}
                          onChange={(e) =>
                            e.target.checked
                              ? setSelectedYears([...selectedYears, year])
                              : setSelectedYears(
                                  selectedYears.filter((y) => y !== year)
                                )
                          }
                        />
                      }
                      label={year}
                      sx={{ display: "block" }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ pb: 2, mb: 2, borderBottom: "1px solid var(--border-primary)" }}>
                <Typography fontWeight={600} mb={1}>
                  {t("products.brand")}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    pr: 1,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {BRANDS.map((brand) => (
                    <FormControlLabel
                      key={brand}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedBrands.includes(brand)}
                          onChange={(e) =>
                            e.target.checked
                              ? setSelectedBrands([...selectedBrands, brand])
                              : setSelectedBrands(
                                  selectedBrands.filter((b) => b !== brand)
                                )
                          }
                        />
                      }
                      label={t(`brands.${brand}`, { defaultValue: brand })}
                      sx={{ display: "block" }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ pb: 2, mb: 2 }}>
                <Typography fontWeight={600} mb={1}>
                  {t("products.medicalSpecialties")}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    pr: 1,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {SPECIALTIES.map((item) => (
                    <FormControlLabel
                      key={item}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedSpecialties.includes(item)}
                          onChange={(e) =>
                            e.target.checked
                              ? setSelectedSpecialties([
                                  ...selectedSpecialties,
                                  item,
                                ])
                              : setSelectedSpecialties(
                                  selectedSpecialties.filter((s) => s !== item)
                                )
                          }
                        />
                      }
                      label={t(`productCategories.${item}`, { defaultValue: item })}
                      sx={{ display: "block" }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              {loading ? (
                <Box
                  sx={{
                    py: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography>{t("common.loading")}</Typography>
                </Box>
              ) : error ? (
                <Box
                  sx={{
                    py: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="error">{error}</Typography>
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Box
                  sx={{
                    py: 8,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t("products.noProductsFound")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("products.adjustFilters")}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                      gap: 3,
                    }}
                  >
                    {filteredProducts
                      .slice(
                        (page - 1) * PRODUCTS_PER_PAGE,
                        page * PRODUCTS_PER_PAGE
                      )
                      .map((product) => {
                        const priceValue =
                          typeof product.zetta_price === "number" &&
                          !isNaN(product.zetta_price)
                            ? product.zetta_price
                            : product.price;
                        const mainImage =
                          product.images && product.images.length > 0
                            ? resolveImageUrl(product.images[0], "/placeholder-product.png")
                            : "/placeholder-product.png";
                        return (
                          <Box key={product.id} sx={{ width: "100%" }}>
                            <Card
                              onClick={() => navigate(`/products/${product.id}`)}
                              sx={{
                                minHeight: 350,
                                borderRadius: 1,
                                boxShadow: "none",
                                cursor: "pointer",
                                border: "1px solid rgba(0, 0, 0, 0.08)",
                                bgcolor: "var(--bg-secondary)",
                                transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
                                "&:hover": {
                                  boxShadow: 2,
                                  borderColor: "var(--border-active)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  overflow: "hidden",
                                  height: 280,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "transparent",
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  image={mainImage}
                                  alt={product.title}
                                  onError={(e) => withImageFallback(e, '/placeholder-product.png')}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    transition: "transform 0.3s ease",
                                  }}
                                />
                              </Box>
                              <CardContent sx={{ p: 2, border: "none" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    mb: 0.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    color: "text.primary",
                                  }}
                                >
                                  {product.title}
                                </Typography>
                                {product.brand && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      mb: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {product.brand}
                                  </Typography>
                                )}
                                {typeof priceValue === "number" && (
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#00a1cc",
                                    }}
                                  >
                                    <span style={{ fontWeight: 600, color: "text.primary", fontSize: "0.875rem" }}>
                                      {t("products.fromPriceLabel", { defaultValue: "From" })}
                                    </span>{" "}
                                    {formatPrice(priceValue)}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                  </Box>

                  <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={handleChangePage}
                      color="primary"
                      shape="rounded"
                    />
                  </Stack>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Container>

      <Box
        sx={{
          backgroundColor: "var(--bg-secondary)",
        }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={3}>
            {t("products.categoryDetails.pageTitle")}
          </Typography>

          <Typography variant="body1" mb={3} color="text.secondary">
            {t("products.categoryDetails.intro")}
          </Typography>

          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            {t("products.categoryDetails.whyTitle")}
          </Typography>
          <Typography variant="body2" mb={3} color="text.secondary">
            {t("products.categoryDetails.whyBody")}
          </Typography>

          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            {t("products.categoryDetails.biomedicalTitle")}
          </Typography>
          <Typography variant="body2" mb={3} color="text.secondary">
            {t("products.categoryDetails.biomedicalBody")}
          </Typography>

          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            {t("products.categoryDetails.experienceTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("products.categoryDetails.experienceBody")}
          </Typography>
        </Container>
      </Box>
    </>
  );
}

export default CategoryDetails;

